import React, { useState } from 'react';
import { Drawer, List, ListItem, ListItemIcon, ListItemText, IconButton, Box, Collapse, ListItemButton } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useNavigate } from 'react-router-dom';
import InventoryIcon from '@mui/icons-material/Inventory';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import { styled } from '@mui/system';
import { Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';

const StyledDrawer = styled(Drawer)(({ theme }: { theme?: Theme }) => ({
    '& .MuiDrawer-paper': {
        backgroundColor: theme?.palette?.background?.default || '#fff',
        boxShadow: theme?.shadows ? theme.shadows[5] : 'none',
        borderRadius: '0 8px 8px 0',
    },
}));

const StyledListItem = styled(ListItem)(({ theme }: { theme?: Theme }) => ({
    display: 'flex',
    alignItems: 'center',
    textAlign: 'left',
    padding: '10px 16px',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    '&:hover': {
        backgroundColor: theme?.palette?.action?.hover || '#f0f0f0',
    },
}));

interface SidebarProps {
    isOpen: boolean;
    onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const [openParents, setOpenParents] = useState<Record<string, boolean>>({});

    const toggleParent = (key: string) => setOpenParents(s => ({ ...s, [key]: !s[key] }));

    const menuStructure: Array<any> = [
        { label: 'Início', icon: <HomeIcon />, path: '/' },
        { label: 'Clientes', icon: <PeopleIcon />, path: '/clients' },
        { label: 'Usuários', icon: <AccountCircleIcon />, path: '/usuarios' },
        {
            label: 'Produtos', icon: <InventoryIcon />, children: [
                { label: 'Lista de Produtos', path: '/produtos' },
                { label: 'Associar Produtos', path: '/clients/products' },
            ],
        },
        {
            label: 'Assinaturas', icon: <SubscriptionsIcon />, children: [
                { label: 'Listagem Assinaturas', path: '/assinaturas/list' },
                { label: 'Solicitar Assinatura', path: '/assinaturas' },
                { label: 'Documentos', path: '/assinaturas/documents' },
            ],
        },
        { label: 'Dashboard', icon: <HomeIcon />, path: '/assinaturas/dashboard' },
        { label: 'Notas Fiscais', icon: <PriceCheckIcon />, path: '/nfse' },
    ];

    return (
        <StyledDrawer anchor="left" open={isOpen} onClose={onClose}>
            <Box
                sx={{
                    width: 250,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: '10px' }}>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <List>
                    {menuStructure.map((item) => (
                        item.children ? (
                            <React.Fragment key={item.label}>
                                <ListItemButton onClick={() => toggleParent(item.label)} sx={{ px: 2 }}>
                                    <ListItemIcon>{item.icon}</ListItemIcon>
                                    <ListItemText primary={item.label} />
                                    {openParents[item.label] ? <ExpandLess /> : <ExpandMore />}
                                </ListItemButton>
                                <Collapse in={Boolean(openParents[item.label])} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {item.children.map((ch: any) => (
                                            <StyledListItem key={ch.label} onClick={() => { navigate(ch.path); onClose(); }} sx={{ pl: 4 }}>
                                                <ListItemIcon />
                                                <ListItemText primary={ch.label} />
                                            </StyledListItem>
                                        ))}
                                    </List>
                                </Collapse>
                            </React.Fragment>
                        ) : (
                            <StyledListItem key={item.label} onClick={() => { navigate(item.path); onClose(); }} sx={{ display: 'flex', alignItems: 'center' }}>
                                <ListItemIcon>{item.icon}</ListItemIcon>
                                <ListItemText primary={item.label} />
                            </StyledListItem>
                        )
                    ))}
                </List>
            </Box>
        </StyledDrawer>
    );
};

export default Sidebar;